import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Calendar, Clock, DollarSign, UserCheck, Layers } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { festivalsAPI, tournamentsAPI, handleApiError } from '../utils/api';

const DEFAULT_COLOR = 'bg-blue-600';

const ManageTournamentsPage = () => {
  const navigate = useNavigate();
  const { festivalId } = useParams();
  const { userRole } = useAuth();

  const [festival, setFestival] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadFestival = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await festivalsAPI.getById(festivalId);
      setFestival(response.data);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [festivalId]);

  useEffect(() => {
    loadFestival();
  }, [loadFestival]);

  // Vérification admin
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">
          Chargement du festival...
        </div>
      </div>
    );
  }

  if (error || !festival) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              {error ? 'Erreur' : 'Festival non trouvé'}
            </h2>
            {error && <p className="text-gray-600 mb-6">{error}</p>}
            <button
              onClick={() => navigate('/manage-festivals')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à la gestion des festivals
            </button>
          </div>
        </div>
      </div>
    );
  }

  const color = festival.circuit?.color || DEFAULT_COLOR;
  const subEvents = festival.subEvents || [];

  const handleDeleteTournament = async (tournament) => {
    if (!window.confirm(`Supprimer le tournoi "${tournament.title}" ? Cette action est irréversible.`)) {
      return;
    }
    setDeletingId(tournament.id);
    try {
      await tournamentsAPI.delete(festivalId, tournament.id);
      setFestival((prev) => ({
        ...prev,
        subEvents: (prev.subEvents || []).filter((t) => t.id !== tournament.id),
      }));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer : ${errTitle} - ${details}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/manage-festivals')}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour à la gestion des festivals
          </button>

          {/* Festival Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className={`${color} bg-opacity-20 border-l-8 ${color.replace('bg-', 'border-')} p-6`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {festival.circuit?.logoUrl && (
                    <img
                      src={festival.circuit.logoUrl}
                      alt="Logo"
                      className="w-16 h-16 flex-shrink-0 object-contain bg-white rounded-lg p-2"
                    />
                  )}
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{festival.title}</h1>
                    <p className="text-gray-600">{festival.description}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                      <span className="text-gray-700">
                        📅 {new Date(festival.startDate).toLocaleDateString('fr-FR')} - {new Date(festival.endDate).toLocaleDateString('fr-FR')}
                      </span>
                      <span className={`${color} text-white px-3 py-1 rounded-full font-semibold`}>
                        {subEvents.length} tournoi{subEvents.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full md:w-auto">
                  <button
                    onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments/create`)}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center whitespace-nowrap"
                  >
                    <Plus className="mr-2" size={20} />
                    Nouveau Tournoi
                  </button>
                  <button
                    onClick={() => navigate(`/manage-festivals/${festivalId}/series`)}
                    className="bg-purple-100 text-purple-800 px-6 py-3 rounded-lg font-semibold hover:bg-purple-200 transition flex items-center justify-center whitespace-nowrap"
                  >
                    <Layers className="mr-2" size={20} />
                    Gérer les séries
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Statistiques */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Total Tournois</p>
                  <p className="text-2xl font-bold text-gray-800">{subEvents.length}</p>
                </div>
                <Calendar className="text-blue-600" size={28} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Participations</p>
                  <p className="text-2xl font-bold text-green-600">
                    {subEvents.filter(t => t.participating).length}
                  </p>
                </div>
                <div className="bg-green-100 p-2 rounded-full">
                  <span className="text-xl">✓</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Buy-in Moyen</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {subEvents.filter(t => t.detailedInfo?.buyIn).length > 0
                      ? Math.round(
                          subEvents
                            .filter(t => t.detailedInfo?.buyIn)
                            .reduce((acc, t) => {
                              const buyIn = parseInt(t.detailedInfo.buyIn.replace(/[^0-9]/g, ''));
                              return acc + (isNaN(buyIn) ? 0 : buyIn);
                            }, 0) / subEvents.filter(t => t.detailedInfo?.buyIn).length
                        ) + '€'
                      : 'N/A'}
                  </p>
                </div>
                <DollarSign className="text-purple-600" size={28} />
              </div>
            </div>
          </div>

          {/* Liste des tournois */}
          <div className="space-y-4">
            {subEvents.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500 mb-4">Aucun tournoi créé pour ce festival</p>
                <button
                  onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments/create`)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
                >
                  Créer le premier tournoi
                </button>
              </div>
            ) : (
              [...subEvents]
                .sort((a, b) => new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time))
                .map(tournament => (
                  <div
                    key={tournament.id}
                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition"
                  >
                    <div className={`border-l-4 ${tournament.participating ? 'border-green-500 bg-green-50' : 'border-gray-300'} p-6`}>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-3 mb-3">
                            <h3 className="text-xl font-bold text-gray-800">{tournament.title}</h3>
                            {tournament.participating && (
                              <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                ✓ Inscrit
                              </span>
                            )}
                          </div>

                          <p className="text-gray-600 mb-3">{tournament.description}</p>

                          {/* Informations principales */}
                          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            {/* Date et heure sur la même ligne sur mobile ; sur écran large,
                                md:contents laisse chaque bloc occuper sa propre cellule de la grille */}
                            <div className="grid grid-cols-2 gap-4 md:contents">
                            <div className="flex items-center gap-2">
                              <Calendar className="text-blue-600" size={18} />
                              <div>
                                <p className="text-xs text-gray-500">Date</p>
                                <p className="text-sm font-semibold text-gray-800">
                                  {new Date(tournament.date).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Clock className="text-blue-600" size={18} />
                              <div>
                                <p className="text-xs text-gray-500">Heure</p>
                                <p className="text-sm font-semibold text-gray-800">{tournament.time?.slice(0, 5)}</p>
                              </div>
                            </div>
                            </div>

                            {tournament.detailedInfo?.buyIn && (
                              <div className="flex items-center gap-2">
                                <DollarSign className="text-green-600" size={18} />
                                <div>
                                  <p className="text-xs text-gray-500">Buy-in</p>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {tournament.detailedInfo.buyIn}
                                  </p>
                                </div>
                              </div>
                            )}

                            {(tournament.detailedInfo?.venue?.name || tournament.detailedInfo?.location) && (
                              <div className="flex items-center gap-2">
                                <span className="text-blue-600">📍</span>
                                <div>
                                  <p className="text-xs text-gray-500">Lieu</p>
                                  <p className="text-sm font-semibold text-gray-800 truncate">
                                    {tournament.detailedInfo?.venue?.name || tournament.detailedInfo?.location}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Informations supplémentaires */}
                          <div className="flex flex-wrap gap-2 text-xs">
                            {tournament.detailedInfo?.startingStack && (
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                🎯 {tournament.detailedInfo.startingStack}
                              </span>
                            )}
                            {tournament.detailedInfo?.blindStructure && (
                              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                ⏱️ {tournament.detailedInfo.blindStructure}
                              </span>
                            )}
                            {tournament.detailedInfo?.duration && (
                              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded">
                                ⏰ {tournament.detailedInfo.duration}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 sm:ml-4 flex-shrink-0">
                          <button
                            onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments/${tournament.id}/participants`)}
                            className="bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition"
                            title="Participants et résultats"
                          >
                            <UserCheck size={20} />
                          </button>
                          <button
                            onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments/edit/${tournament.id}`)}
                            className="bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition"
                            title="Modifier"
                          >
                            <Edit size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteTournament(tournament)}
                            disabled={deletingId === tournament.id}
                            className="bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                            title="Supprimer"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTournamentsPage;
